/**
 * src/pages/Scraping.jsx
 * Panel de control de scraping: trigger manual, estado y logs.
 */
import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Header } from '../components/layout/Header'
import { Card } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Badge, RESULTADO_COLOR } from '../components/ui/Badge'
import { Table } from '../components/ui/Table'
import { useEstado, useLogs, useTrigger } from '../hooks/useScraping'
import { useCliente } from '../hooks/useClientes'

function EstadoSection({ estado }) {
  if (!estado) return null

  function Block({ label, data }) {
    return (
      <div style={{
        padding: '14px', background: 'var(--color-surface-2)',
        border: '1px solid var(--color-border)', borderRadius: '8px',
        flex: 1,
      }}>
        <div style={{
          fontSize: '10px', fontWeight: 500, color: 'var(--color-text-3)',
          fontFamily: 'var(--font-mono)', letterSpacing: '0.1em', marginBottom: '10px',
        }}>
          {label}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {[
            ['Estado', <Badge color={data.activo ? 'green' : 'gray'} dot pulse={data.activo}>{data.activo ? 'Activo' : 'Inactivo'}</Badge>],
            ['Frecuencia', <span style={{ fontFamily: 'var(--font-mono)', fontSize: '12px', color: 'var(--color-text)' }}>{data.frecuencia}</span>],
            ['Último run', data.ultimo_run
              ? <span style={{ fontFamily: 'var(--font-mono)', fontSize: '11px', color: 'var(--color-text-2)' }}>{new Date(data.ultimo_run).toLocaleString('es-CL')}</span>
              : <span style={{ color: 'var(--color-text-3)', fontSize: '12px' }}>—</span>],
            ['Próximo run', data.proximo_run
              ? <span style={{ fontFamily: 'var(--font-mono)', fontSize: '11px', color: 'var(--color-text-2)' }}>{new Date(data.proximo_run).toLocaleString('es-CL')}</span>
              : <span style={{ color: 'var(--color-text-3)', fontSize: '12px' }}>—</span>],
          ].map(([k, v]) => (
            <div key={k} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '12px', color: 'var(--color-text-3)' }}>{k}</span>
              {v}
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
      <Block label="PRECIOS" data={estado.precios} />
      <Block label="ÓRDENES DE COMPRA" data={estado.ocs} />
    </div>
  )
}

function TriggerPanel({ clienteId, onSuccess }) {
  const trigger = useTrigger(clienteId)
  const [tipo, setTipo] = useState('ambos')
  const [result, setResult] = useState(null)

  const TIPOS = [
    { value: 'precios', label: 'Precios', color: '#3b82f6' },
    { value: 'ocs', label: 'OCs', color: '#f59e0b' },
    { value: 'ambos', label: 'Ambos', color: 'var(--color-accent)' },
  ]

  async function handleTrigger() {
    setResult(null)
    try {
      const data = await trigger.mutateAsync({ tipo })
      setResult({ ok: data.ok, duracion: data.duracion_s, mensaje: data.mensaje })
      onSuccess?.()
    } catch (e) {
      setResult({ ok: false, mensaje: e.response?.data?.detail || 'Error al ejecutar' })
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
      {/* Tipo selector */}
      <div>
        <div style={{
          fontSize: '11px', fontWeight: 500, color: 'var(--color-text-3)',
          fontFamily: 'var(--font-mono)', letterSpacing: '0.08em', marginBottom: '8px',
        }}>
          TIPO DE SCRAPING
        </div>
        <div style={{ display: 'flex', gap: '6px' }}>
          {TIPOS.map(t => (
            <button
              key={t.value}
              onClick={() => setTipo(t.value)}
              style={{
                padding: '7px 16px', borderRadius: '6px', fontSize: '13px',
                fontWeight: 500, cursor: 'pointer', transition: 'all 0.15s',
                border: `1px solid ${tipo === t.value ? t.color : 'var(--color-border)'}`,
                background: tipo === t.value ? `${t.color}18` : 'transparent',
                color: tipo === t.value ? t.color : 'var(--color-text-3)',
                fontFamily: 'var(--font-ui)',
              }}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Botón trigger */}
      <Button
        onClick={handleTrigger}
        loading={trigger.isPending}
        icon={
          !trigger.isPending && (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polygon points="5 3 19 12 5 21 5 3" />
            </svg>
          )
        }
      >
        {trigger.isPending ? 'Ejecutando pipeline...' : `Ejecutar · ${tipo}`}
      </Button>

      {/* Resultado */}
      {result && (
        <div style={{
          padding: '10px 12px', borderRadius: '7px',
          background: result.ok ? 'rgba(16,185,129,0.08)' : 'rgba(239,68,68,0.08)',
          border: `1px solid ${result.ok ? 'rgba(16,185,129,0.2)' : 'rgba(239,68,68,0.2)'}`,
          fontSize: '12px', fontFamily: 'var(--font-mono)',
          color: result.ok ? 'var(--color-ok)' : 'var(--color-error)',
        }}>
          {result.ok ? '✓' : '✗'} {result.mensaje}
          {result.duracion != null && <span style={{ color: 'var(--color-text-3)', marginLeft: '8px' }}>{result.duracion}s</span>}
        </div>
      )}
    </div>
  )
}

const LOG_COLS = [
  {
    key: 'tipo', label: 'Tipo', mono: true, nowrap: true,
    render: v => <span style={{ fontFamily: 'var(--font-mono)', fontSize: '12px' }}>{v}</span>,
  },
  {
    key: 'inicio', label: 'Inicio', nowrap: true,
    render: v => v ? new Date(v).toLocaleString('es-CL') : '—',
  },
  {
    key: 'fin', label: 'Fin', nowrap: true,
    render: v => v ? new Date(v).toLocaleString('es-CL') : '—',
  },
  {
    key: 'resultado', label: 'Resultado', width: '100px',
    render: v => v
      ? <Badge color={RESULTADO_COLOR[v] || 'gray'} dot pulse={v === 'running'}>{v}</Badge>
      : <Badge color="gray">—</Badge>,
  },
]

export default function Scraping() {
  const { clienteId } = useParams()
  const navigate = useNavigate()

  const { data: cliente } = useCliente(clienteId)
  const { data: estado, isLoading: loadingEstado } = useEstado(clienteId)
  const { data: logs = [], isLoading: loadingLogs } = useLogs(clienteId, 30)

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Header
        title="Scraping"
        subtitle={cliente ? `${cliente.nombre} · ${clienteId}` : clienteId}
        action={
          <Button size="sm" variant="ghost" onClick={() => navigate(`/clientes/${clienteId}`)}>
            ← Cliente
          </Button>
        }
      />

      <div style={{ flex: 1, overflow: 'auto', padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: '16px', alignItems: 'start' }}>
          {/* Estado */}
          <Card title="Estado del scheduler" subtitle="Configuración actual">
            {loadingEstado ? (
              <div style={{ color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)', fontSize: '12px' }}>Cargando...</div>
            ) : (
              <EstadoSection estado={estado} />
            )}
          </Card>

          {/* Trigger */}
          <Card title="Ejecución manual">
            <TriggerPanel clienteId={clienteId} />
          </Card>
        </div>

        {/* Logs */}
        <Card
          title="Historial de runs"
          subtitle={`${logs.length} ejecuciones recientes`}
        >
          <Table
            columns={LOG_COLS}
            data={logs}
            loading={loadingLogs}
            emptyMessage="Sin ejecuciones registradas"
          />
        </Card>
      </div>
    </div>
  )
}
